
import { Suspense, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowLeft, Download, Maximize2, Minimize2 } from "lucide-react";
import { useLocale, useTranslations } from "@/i18n";
import { SlideContext } from "@/components/deck/_context";
import { getLazySlide, preloadSlide } from "@/components/deck/_lazySlides";
import { DeckOverview } from "@/components/deck/DeckOverview";
import { DeckProgress } from "@/components/deck/DeckProgress";
import { openPresenterWindow } from "@/components/deck/presenterWindow";
import { getVisibleSlides } from "@/decks/registry";
import type { DeckDef } from "@/decks/types";
import { useDeckAnalytics } from "@/hooks/useDeckAnalytics";
import { useDeckNavigation } from "@/hooks/useDeckNavigation";
import { usePreviewTextEditing } from "@/hooks/usePreviewTextEditing";
import { useDeckAudienceSync } from "@/hooks/useDeckSync";

type Overlay = "none" | "overview";
const SOTA_URL = (import.meta.env.VITE_SOTA_URL ?? "https://sotaops.com").replace(
  /\/+$/,
  "",
);

export function DeckShell({ deck }: { deck: DeckDef }) {
  const t = useTranslations("deckViewer");
  // Solo resuelve los nombres de slide para la navegación: fuera del registro del preview
  // editable, para que no compitan con el texto que la slide sí pinta.
  const tDeck = useTranslations(`deck.${deck.id}`, { untraced: true });
  const locale = useLocale();
  const reduced = useReducedMotion();
  const [overlay, setOverlay] = useState<Overlay>("none");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const slidesRef = useRef<HTMLDivElement>(null);

  const slides = useMemo(() => getVisibleSlides(deck), [deck]);
  const nav = useDeckNavigation({
    slideIds: slides.map((slide) => slide.id),
    fragmentCounts: slides.map((slide) => slide.fragments ?? 0),
    enabled: overlay === "none",
    onEscape: () =>
      setOverlay((current) => (current === "none" ? "overview" : "none")),
    // N abre la vista de presentador en otra ventana. Las notas no viajan a este visor:
    // esa vista las pide autenticada (docs/vista-presentador.md).
    onToggleNotes: () => openPresenterWindow(locale, deck.id),
  });

  useEffect(() => {
    // Precargar ambos vecinos evita que el chunk lazy suspenda al navegar (adelante
    // o atrás) y muestre el fallback, que es la causa del parpadeo entre slides.
    for (const slide of [slides[nav.index + 1], slides[nav.index - 1]]) {
      if (slide) preloadSlide(deck.id, slide.id, slide.load);
    }
  }, [deck.id, nav.index, slides]);

  const currentSlide = slides[nav.index];
  useDeckAnalytics({
    deckId: deck.id,
    slideId: currentSlide.id,
    slideIndex: nav.index,
  });
  // Se resuelve en cada render (no al montar): así una vecina ya precargada entra con
  // su componente real en vez de un lazy() que suspendería.
  const CurrentComponent = getLazySlide(deck.id, currentSlide.id, currentSlide.load);
  const titles = slides.map((slide) => tDeck(`slides.${slide.id}.title`));
  usePreviewTextEditing({
    deckId: deck.id,
    slideId: currentSlide.id,
    containerRef: slidesRef,
  });

  useDeckAudienceSync({
    deckId: deck.id,
    locale,
    index: nav.index,
    slideIds: slides.map((slide) => slide.id),
    titles,
    onNext: nav.next,
    onPrev: nav.prev,
    onGoto: nav.goto,
  });

  useEffect(() => {
    function syncFullscreen() {
      setIsFullscreen(document.fullscreenElement === rootRef.current);
    }
    document.addEventListener("fullscreenchange", syncFullscreen);
    return () => document.removeEventListener("fullscreenchange", syncFullscreen);
  }, []);

  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }
      await rootRef.current?.requestFullscreen();
    } catch {
      setIsFullscreen(false);
    }
  }

  // Tecla F: entra y sale de pantalla completa (salvo al escribir en un campo).
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "f" && event.key !== "F") return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (target instanceof Element && target.closest("input, textarea, select")) return;
      event.preventDefault();
      if (document.fullscreenElement) {
        void document.exitFullscreen().catch(() => undefined);
      } else {
        void rootRef.current?.requestFullscreen().catch(() => undefined);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  function handleSlideClick(event: MouseEvent<HTMLDivElement>) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest("a, button, input, textarea, select, [role='button']")) {
      return;
    }
    const bounds = event.currentTarget.getBoundingClientRect();
    const midpoint = bounds.left + bounds.width / 2;
    if (event.clientX < midpoint) {
      nav.prev();
    } else {
      nav.next();
    }
  }

  // Fundido cruzado en vez de deslizamiento lateral: la slide entrante aparece por
  // encima (zIndex creciente) haciendo fade-in sobre la saliente, que permanece opaca
  // debajo hasta desmontarse. Sin salto horizontal y sin dejar ver el fondo del root.
  // El exit baja a 0.99 (imperceptible) a propósito: si no cambia ningún valor, motion da
  // la salida por terminada al instante, desmonta la saliente y la entrante hace fade-in
  // sobre el fondo negro del root (pestañeo a negro entre slides).
  const variants = {
    enter: reduced ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 },
    center: { opacity: 1, y: 0 },
    exit: { opacity: 0.99, y: 0 },
  };
  const zRef = useRef(0);
  const zIndex = useMemo(() => (zRef.current += 1), [nav.index]);

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 select-none overflow-hidden overscroll-none bg-landing-black"
    >
      {/* Contexto de apilamiento propio: el z-index de las slides crece con cada cambio y,
          sin aislarlo, a las ~30 navegaciones tapaba los controles y las notas. */}
      <div ref={slidesRef} className="absolute inset-0 isolate">
        <AnimatePresence initial={false}>
          <motion.div
            key={currentSlide.id}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            onClick={handleSlideClick}
            transition={{ duration: reduced ? 0.15 : 0.4, ease: [0.4, 0, 0.2, 1] }}
            style={{ zIndex }}
            className="absolute inset-0"
          >
            <SlideContext.Provider
              value={{
                activeFragment: nav.fragment,
                isActive: true,
                number: nav.index + 1,
              }}
            >
              <Suspense fallback={<div className="h-full w-full" />}>
                <CurrentComponent activeFragment={nav.fragment} isActive />
              </Suspense>
            </SlideContext.Provider>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="absolute right-4 top-4 z-30 flex items-center gap-2 [@media(max-height:500px)]:right-2 [@media(max-height:500px)]:top-2 [@media(max-height:500px)]:flex-col-reverse">
        {!isFullscreen ? (
          <a
            href={`${SOTA_URL}/${locale}/recursos`}
            aria-label={t("backToResources")}
            title={t("backToResources")}
            className="inline-flex items-center gap-2 rounded-full bg-black/50 px-3 py-2 text-[11px] font-medium text-white/75 backdrop-blur-sm transition-colors hover:text-white"
          >
            <ArrowLeft size={15} />
            <span className="hidden sm:inline [@media(max-height:500px)]:hidden">{t("backToResources")}</span>
          </a>
        ) : null}

        <button
          type="button"
          onClick={() => void toggleFullscreen()}
          aria-label={t(isFullscreen ? "exitFullscreen" : "enterFullscreen")}
          title={t(isFullscreen ? "exitFullscreen" : "enterFullscreen")}
          className="rounded-full bg-black/50 p-2.5 text-white/70 backdrop-blur-sm transition-colors hover:text-white"
        >
          {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
      </div>

      <a
        href={`/pdf/${deck.id}.${locale}.pdf`}
        download
        aria-label={t("downloadPdf")}
        title={t("downloadPdf")}
        className="absolute bottom-4 right-4 z-30 rounded-full bg-black/50 p-2.5 text-white/70 backdrop-blur-sm transition-colors hover:text-white [@media(max-height:500px)]:bottom-2 [@media(max-height:500px)]:right-2"
      >
        <Download size={16} />
      </a>

      <p className="pointer-events-none absolute bottom-5 left-4 z-30 hidden max-w-xs text-[11px] text-white/45 md:block [@media(max-height:500px)]:md:hidden">
        {t("hint")}
      </p>

      <div className="pointer-events-none absolute inset-x-0 bottom-4 z-30 flex justify-center [@media(max-height:500px)]:bottom-0 [@media(max-height:500px)]:origin-bottom [@media(max-height:500px)]:scale-[0.7]">
        <DeckProgress
          count={nav.count}
          current={nav.index}
          titles={titles}
          onSelect={(index) => {
            nav.goto(index);
            setOverlay("none");
          }}
        />
      </div>

      <div aria-live="polite" className="sr-only">
        {t("slideAnnouncement", {
          current: nav.index + 1,
          total: nav.count,
          title: titles[nav.index],
        })}
      </div>

      <AnimatePresence>
        {overlay === "overview" ? (
          <DeckOverview
            titles={titles}
            current={nav.index}
            onSelect={(index) => {
              nav.goto(index);
              setOverlay("none");
            }}
            onClose={() => setOverlay("none")}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}
